import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Building2, Eye, Pencil, Plus, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { departmentMasterAPI } from '../services/api';
import { getMasterCrudAccess } from '../lib/masterData';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';
import { EmptyState, CardSkeleton } from '../components/common/States';
import { StatCard } from '../components/common/StatCard';

const AdminDepartmentsMasterPage = ({ routeBasePrefix = '/admin', dashboardHomePath = '/admin' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(true);
  const access = useMemo(() => getMasterCrudAccess(user), [user]);
  const departmentsPath = `${routeBasePrefix}/master/departments`;
  const subDepartmentsPath = `${routeBasePrefix}/master/subdepartments`;
  const designationsPath = `${routeBasePrefix}/master/designations`;
  const importPath = `${routeBasePrefix}/import/department_master`;

  const loadRows = useCallback(async () => {
    try {
      setLoading(true);
      const res = await departmentMasterAPI.list({ include_inactive: includeInactive });
      setRows(res.data || []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load departments'));
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    const successMessage = location.state?.masterSuccessMessage;
    if (!successMessage) return;
    toast.success(successMessage);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-foreground">Department Master</h2>
          <p className="text-sm text-muted-foreground">Manage departments with full CRUD and status toggle.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={() => navigate(importPath)}>Import Departments</Button>
          <Button variant="outline" onClick={() => navigate(subDepartmentsPath)}>Sub-Departments</Button>
          <Button variant="outline" onClick={() => navigate(designationsPath)}>Designations</Button>
          {access.canCreate ? (
            <Button onClick={() => navigate(`${departmentsPath}/new`)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Department
            </Button>
          ) : null}
          <Button type="button" variant="outline" onClick={() => navigate(dashboardHomePath)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>
      </div>

      <StatCard label="Total Departments" value={rows.length} icon={Building2} tone="primary" />

      <Card>
        <CardHeader><CardTitle>Departments Table</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2">
            <Checkbox id="department-include-inactive" checked={includeInactive} onCheckedChange={(v) => setIncludeInactive(Boolean(v))} />
            <Label htmlFor="department-include-inactive">Show Inactive</Label>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <CardSkeleton rows={5} />
                  </TableCell>
                </TableRow>
              ) : null}
              {!loading && rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <EmptyState
                      icon={Building2}
                      title="No departments yet"
                      description="Create a department to get started."
                      {...(access.canCreate ? { actionLabel: 'Add Department', onAction: () => navigate(`${departmentsPath}/new`) } : {})}
                    />
                  </TableCell>
                </TableRow>
              ) : null}
              {rows.map((row) => (
                <TableRow key={row.id} className="card-interactive">
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.location || '-'}</TableCell>
                  <TableCell>
                    <Badge variant={row.is_active ? 'success' : 'secondary'}>{row.is_active ? 'Active' : 'Inactive'}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {access.canView ? (
                        <Button size="icon" variant="outline" title="View" onClick={() => navigate(`${departmentsPath}/${row.id}/view`)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      ) : null}
                      {access.canEdit ? (
                        <Button size="icon" variant="outline" title="Edit" onClick={() => navigate(`${departmentsPath}/${row.id}/edit`)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                      ) : null}
                      {access.canDelete ? (
                        <Button size="icon" variant="outline" title="Delete" onClick={() => navigate(`${departmentsPath}/${row.id}/delete`)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      ) : null}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDepartmentsMasterPage;
